import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';
import { stageOdontogramData } from '../../utils/stageOdontogramData';

export const medidentGeDefaultFormData = {
  etat_denture: [],
  traitement_envisage: [],
};

const WHOLE_TOOTH_FLAGS = [
  'dent_manquante',
  'couronnee',
  'fracture',
  'implant',
  'restes_radiculaires',
  'element_de_pont',
  'bouton_pression',
  'prothe',
  'traitement_radiculaire',
];

const FACE_OPTIONS = ['V', 'P', 'L', 'O', 'I', 'M', 'D'];
const CONDITION_OPTIONS = ['caries', 'obture', 'crochets', 'appui'];
const DEFAULT_FACE = 'V';

const PERMANENT_TEETH = [
  ...Array.from({ length: 8 }, (_, i) => String(11 + i)),
  ...Array.from({ length: 8 }, (_, i) => String(21 + i)),
  ...Array.from({ length: 8 }, (_, i) => String(31 + i)),
  ...Array.from({ length: 8 }, (_, i) => String(41 + i)),
];

const TEMPORARY_TEETH = [
  ...Array.from({ length: 5 }, (_, i) => String(51 + i)),
  ...Array.from({ length: 5 }, (_, i) => String(61 + i)),
  ...Array.from({ length: 5 }, (_, i) => String(71 + i)),
  ...Array.from({ length: 5 }, (_, i) => String(81 + i)),
];

const FDI_OPTIONS = [...PERMANENT_TEETH, ...TEMPORARY_TEETH];

/** Same visual pattern as the consultation role Listbox. */
function AppSelect({ value, onChange, options, placeholder, className = 'w-28' }) {
  const selected = options.find((opt) => opt.value === value) || null;
  const label = selected ? selected.label : placeholder;

  return (
    <div className={`relative ${className}`}>
      <Listbox value={value} onChange={onChange}>
        {({ open }) => (
          <>
            <Listbox.Button className="relative w-full text-left bg-[#E0EAFE] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <span className={`block truncate pr-8 ${selected ? 'text-gray-900' : 'text-gray-500'}`}>
                {label}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDownIcon
                  className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                    open ? 'transform rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-20 mt-1 w-full bg-white rounded-md shadow-lg max-h-60 py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                {options.map((opt) => (
                  <Listbox.Option
                    key={opt.value === '' ? '__empty' : opt.value}
                    value={opt.value}
                    className={({ active, selected: isSelected }) =>
                      `relative cursor-pointer select-none py-2 px-3 ${
                        active ? 'bg-[#E0EAFE] text-gray-900' : 'text-gray-900'
                      } ${isSelected ? 'bg-[#E0EAFE]' : ''}`
                    }
                  >
                    {({ selected: isSelected }) => (
                      <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}>
                        {opt.label}
                      </span>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>
    </div>
  );
}

function debounce(fn, delay) {
  let timer = null;
  const wrapped = (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
  wrapped.cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  return wrapped;
}

function odontogramSnapshot(data) {
  return JSON.stringify(normalizeOdontogram(data || medidentGeDefaultFormData));
}

function emptyTooth(dent = '') {
  return {
    dent,
    dent_manquante: false,
    couronnee: false,
    fracture: false,
    implant: false,
    restes_radiculaires: false,
    element_de_pont: false,
    bouton_pression: false,
    prothe: false,
    traitement_radiculaire: false,
    faces: [],
  };
}

function normalizeOdontogram(data) {
  const source = data && typeof data === 'object' ? data : {};
  return {
    etat_denture: Array.isArray(source.etat_denture) ? source.etat_denture.map(normalizeTooth) : [],
    traitement_envisage: Array.isArray(source.traitement_envisage)
      ? source.traitement_envisage.map(normalizeTooth)
      : [],
  };
}

function normalizeTooth(tooth) {
  const base = emptyTooth(String(tooth?.dent || ''));
  WHOLE_TOOTH_FLAGS.forEach((flag) => {
    base[flag] = Boolean(tooth?.[flag]);
  });
  base.faces = Array.isArray(tooth?.faces)
    ? tooth.faces
        .filter((f) => f && typeof f === 'object')
        .map((f) => ({
          face: FACE_OPTIONS.includes(f.face) ? f.face : DEFAULT_FACE,
          conditions: Array.isArray(f.conditions) ? [...f.conditions] : [],
        }))
    : [];
  return base;
}

/** Strip false flags and empty faces for a cleaner payload to the extension / LLM round-trips. */
export function compactOdontogram(data) {
  const compactTooth = (tooth) => {
    const out = { dent: String(tooth.dent || '') };
    WHOLE_TOOTH_FLAGS.forEach((flag) => {
      if (tooth[flag]) out[flag] = true;
    });
    const faces = (tooth.faces || [])
      .filter((f) => f.face && Array.isArray(f.conditions) && f.conditions.length > 0)
      .map((f) => ({ face: f.face, conditions: f.conditions }));
    if (faces.length) out.faces = faces;
    return out;
  };

  return {
    etat_denture: (data.etat_denture || []).filter((t) => t.dent).map(compactTooth),
    traitement_envisage: (data.traitement_envisage || []).filter((t) => t.dent).map(compactTooth),
  };
}

const ToothEditor = ({ tooth, onChange, onRemove, t }) => {
  const updateFlag = (flag, checked) => {
    onChange({ ...tooth, [flag]: checked });
  };

  const addFace = () => {
    const used = new Set((tooth.faces || []).map((f) => f.face));
    const nextFace = FACE_OPTIONS.find((f) => !used.has(f)) || DEFAULT_FACE;
    onChange({
      ...tooth,
      faces: [...(tooth.faces || []), { face: nextFace, conditions: ['caries'] }],
    });
  };

  const updateFace = (index, patch) => {
    const faces = [...(tooth.faces || [])];
    const next = { ...faces[index], ...patch };
    if (!FACE_OPTIONS.includes(next.face)) next.face = DEFAULT_FACE;
    faces[index] = next;
    onChange({ ...tooth, faces });
  };

  const removeFace = (index) => {
    onChange({ ...tooth, faces: (tooth.faces || []).filter((_, i) => i !== index) });
  };

  const toggleCondition = (faceIndex, condition) => {
    const faces = [...(tooth.faces || [])];
    const current = new Set(faces[faceIndex].conditions || []);
    if (current.has(condition)) current.delete(condition);
    else current.add(condition);
    faces[faceIndex] = { ...faces[faceIndex], conditions: [...current] };
    onChange({ ...tooth, faces });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
      <div className="flex items-center justify-between gap-3">
        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
          {t('medidentGe.tooth')}
          <AppSelect
            value={tooth.dent || ''}
            onChange={(dent) => onChange({ ...tooth, dent })}
            placeholder={t('medidentGe.selectTooth')}
            className="w-36"
            options={[
              { value: '', label: t('medidentGe.selectTooth') },
              ...FDI_OPTIONS.map((id) => ({ value: id, label: id })),
            ]}
          />
        </label>
        <button
          type="button"
          onClick={onRemove}
          className="text-sm text-red-600 hover:text-red-800"
        >
          {t('medidentGe.removeTooth')}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {WHOLE_TOOTH_FLAGS.map((flag) => (
          <label key={flag} className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={Boolean(tooth[flag])}
              onChange={(e) => updateFlag(flag, e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[rgb(141,212,209)] accent-[rgb(141,212,209)] focus:ring-[rgb(141,212,209)]"
            />
            {t(`medidentGe.flags.${flag}`)}
          </label>
        ))}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h5 className="text-sm font-medium text-gray-800">{t('medidentGe.faces')}</h5>
          <button
            type="button"
            onClick={addFace}
            className="text-sm text-[rgb(20,140,135)] hover:text-[rgb(15,110,105)]"
          >
            {t('medidentGe.addFace')}
          </button>
        </div>
        {(tooth.faces || []).map((face, faceIndex) => (
          <div key={faceIndex} className="flex flex-wrap items-center gap-2 rounded-md bg-gray-50 p-2">
            <AppSelect
              value={FACE_OPTIONS.includes(face.face) ? face.face : DEFAULT_FACE}
              onChange={(nextFace) => updateFace(faceIndex, { face: nextFace || DEFAULT_FACE })}
              className="w-44"
              options={FACE_OPTIONS.map((opt) => ({
                value: opt,
                label: `${opt}: ${t(`medidentGe.faceLabels.${opt}`)}`,
              }))}
            />
            <div className="flex flex-wrap gap-2">
              {CONDITION_OPTIONS.map((condition) => (
                <label key={condition} className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={(face.conditions || []).includes(condition)}
                    onChange={() => toggleCondition(faceIndex, condition)}
                    className="h-4 w-4 rounded border-gray-300 text-[rgb(141,212,209)] accent-[rgb(141,212,209)] focus:ring-[rgb(141,212,209)]"
                  />
                  {t(`medidentGe.conditions.${condition}`)}
                </label>
              ))}
            </div>
            <button
              type="button"
              onClick={() => removeFace(faceIndex)}
              className="text-sm text-red-600 hover:text-red-800 ml-auto"
            >
              {t('medidentGe.removeFace')}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const ToothSection = ({ title, teeth, onChange, t }) => {
  const updateTooth = (index, next) => {
    const list = [...teeth];
    list[index] = next;
    onChange(list);
  };

  const removeTooth = (index) => {
    onChange(teeth.filter((_, i) => i !== index));
  };

  const addTooth = () => {
    onChange([...teeth, emptyTooth()]);
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-base font-semibold text-gray-900">{title}</h4>
        <button
          type="button"
          onClick={addTooth}
          className="inline-flex items-center rounded-md bg-[rgb(141,212,209)] px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-[rgb(121,192,189)]"
        >
          {t('medidentGe.addTooth')}
        </button>
      </div>
      {teeth.length === 0 ? (
        <p className="text-sm text-gray-500">{t('medidentGe.emptySection')}</p>
      ) : (
        teeth.map((tooth, index) => (
          <ToothEditor
            key={index}
            tooth={tooth}
            onChange={(next) => updateTooth(index, next)}
            onRemove={() => removeTooth(index)}
            t={t}
          />
        ))
      )}
    </section>
  );
};

const MedidentGeForm = forwardRef(function MedidentGeForm({ initialData, onSave }, ref) {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState(() => normalizeOdontogram(initialData || medidentGeDefaultFormData));
  const [stageMessage, setStageMessage] = useState(null);
  const [staging, setStaging] = useState(false);
  const formDataRef = useRef(formData);
  const lastEmittedSnapshotRef = useRef(odontogramSnapshot(initialData));
  const onSaveRef = useRef(onSave);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const debouncedSave = useRef(
    debounce((data) => {
      const normalized = normalizeOdontogram(data);
      // Keep incomplete teeth (no FDI yet) in parent state — compact only for extension/clipboard.
      lastEmittedSnapshotRef.current = odontogramSnapshot(normalized);
      if (typeof onSaveRef.current === 'function') {
        onSaveRef.current(normalized);
      }
    }, 500)
  ).current;

  // Sync from parent only for external updates (LLM generate/correct), not our own saves.
  useEffect(() => {
    const incoming = odontogramSnapshot(initialData);
    if (incoming === lastEmittedSnapshotRef.current) {
      return;
    }
    debouncedSave.cancel();
    lastEmittedSnapshotRef.current = incoming;
    setFormData(normalizeOdontogram(initialData || medidentGeDefaultFormData));
  }, [initialData, debouncedSave]);

  useImperativeHandle(ref, () => ({
    getFormData: () => normalizeOdontogram(formDataRef.current),
    flushSave: () => {
      debouncedSave.cancel();
      const normalized = normalizeOdontogram(formDataRef.current);
      lastEmittedSnapshotRef.current = odontogramSnapshot(normalized);
      if (typeof onSaveRef.current === 'function') {
        onSaveRef.current(normalized);
      }
      return normalized;
    },
  }));

  const updateSection = (key, list) => {
    setFormData((prev) => {
      const next = { ...prev, [key]: list };
      debouncedSave(next);
      return next;
    });
  };

  const handleStageToExtension = async () => {
    setStaging(true);
    setStageMessage(null);
    const payload = compactOdontogram(formDataRef.current);
    try {
      const result = await stageOdontogramData(payload);
      if (result?.ok) {
        setStageMessage({
          type: 'success',
          text: t('medidentGe.stageSuccess', { count: result.findingsCount ?? 0 }),
        });
      } else {
        setStageMessage({
          type: 'error',
          text: result?.error || t('medidentGe.stageError'),
        });
      }
    } catch (err) {
      setStageMessage({ type: 'error', text: t('medidentGe.stageError') });
    } finally {
      setStaging(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('medidentGe.title')}</h3>
          <p className="text-sm text-gray-500">{t('medidentGe.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={handleStageToExtension}
          disabled={staging}
          className="inline-flex items-center rounded-md bg-[rgb(141,212,209)] px-3 py-2 text-sm font-medium text-gray-800 hover:bg-[rgb(121,192,189)] disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2"
        >
          {staging ? t('medidentGe.staging') : t('medidentGe.sendToExtension')}
        </button>
      </div>

      {stageMessage && (
        <div
          className={`rounded-md p-3 text-sm ${
            stageMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
        >
          {stageMessage.text}
        </div>
      )}

      <ToothSection
        title={t('medidentGe.etatDenture')}
        teeth={formData.etat_denture}
        onChange={(list) => updateSection('etat_denture', list)}
        t={t}
      />

      <ToothSection
        title={t('medidentGe.traitementEnvisage')}
        teeth={formData.traitement_envisage}
        onChange={(list) => updateSection('traitement_envisage', list)}
        t={t}
      />
    </div>
  );
});

export default MedidentGeForm;
